import dayjs, { type Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';

dayjs.extend(isBetween);
dayjs.extend(utc);

export type { Dayjs as dateTimeType };

export function getToday(): Dayjs {
  return dayjs();
}

export function normalizeToDateOnly(date: any) {
  return dayjs(date).startOf('day');
}

export function toDateTime(date: string | Date | Dayjs): Dayjs {
  // If the date string ends with Z, it's a UTC date
  if (typeof date === 'string' && date.endsWith('Z')) {
    return dayjs.utc(date);
  }
  return dayjs(date);
}

export function checkValidDate(date: string | Date | Dayjs): boolean {
  return dayjs(date).isValid();
}

export function formatDateWithTime(date: string | Date, format: string = 'DD MMM YYYY HH:mm:ss'): string {
  if (!date) return '';
  return dayjs(date).format(format);
}

export function formatDate(date: string | Date, format: string = 'DD MMM YYYY'): string {
  return dayjs(date).format(format);
}

export function formatDateWithLocal(date: string | Date, format: string = 'DD MMM YYYY'): string {
  const d = dayjs.utc(date);
  return d.format(format);
}

export function formatDateWithLocalSlash(date: string | Date, format: string = 'DD/MM/YYYY'): string {
  const d = dayjs.utc(date);
  return d.format(format);
}

export function formatDateWithSlash(date: string | Date, format: string = 'DD/MM/YYYY'): string {
  return dayjs(date).format(format);
}

export function formatDateUTC(date: string | Date, format: string = 'DD MMM YYYY'): string {
  return dayjs.utc(date).format(format);
}

export function formatDateToISOString(date: string | Date): string {
  return dayjs(date).toISOString();
}

export function getTodayFormatted(format: string = 'DD MMM YYYY'): string {
  return dayjs().format(format);
}

export function addDays(date: string | Date, days: number, format: string = 'DD MMM YYYY'): string {
  return dayjs(date).add(days, 'day').format(format);
}

export function subtractDays(date: string | Date, days: number, format: string = 'DD MMM YYYY'): string {
  return dayjs(date).subtract(days, 'day').format(format);
}

export function addMonths(date: string | Date | Dayjs, months: number, format?: string): string | Date {
  const originalDate = dayjs(date);
  const originalDay = originalDate.date();

  // Add months and check if we need to adjust the day
  const newDate = originalDate.add(months, 'month');
  const daysInNewMonth = newDate.daysInMonth();

  // Use either original day or last day of the new month, whichever is smaller
  const targetDay = Math.min(originalDay, daysInNewMonth);
  const finalDate = newDate.date(targetDay).hour(12).minute(0).second(0).millisecond(0);

  // Return Date object if no format specified, otherwise return formatted string
  return format ? finalDate.format(format) : finalDate.toDate();
}

export function isAfter(date1: string | Date, date2: string | Date): boolean {
  return dayjs(date1).isAfter(date2);
}

export function isBefore(date1: string | Date, date2: string | Date): boolean {
  return dayjs(date1).isBefore(date2);
}

export function getCurrentYear(): number {
  return dayjs().year();
}

export function getEndOfDay(): Dayjs {
  return dayjs().endOf('day');
}

export function getMonthName(monthIndex: number): string {
  return dayjs().month(monthIndex).format('MMM');
}

export function getStartOfDayISOString(date: string | Date | Dayjs): string {
  return dayjs(date).startOf('day').toISOString();
}

export function getStartOfDayUTCISOString(date: string | Date | Dayjs): string {
  return dayjs(date).utc().startOf('day').toISOString();
}

export function getStartOfDayUTC(date: string | Date | Dayjs): string {
  return dayjs.utc(date).startOf('day').toISOString();
}

export function getFirstDayOfCurrentFinancialYear(): Dayjs {
  const currentYear = dayjs().year();
  const currentMonth = dayjs().month();
  const firstDayOfFinancialYear = dayjs(`${currentMonth < 3 ? currentYear - 1 : currentYear}-04-01`);
  return firstDayOfFinancialYear;
}

export function formatDateToShortFormat(date: string | Date | Dayjs): string {
  return dayjs(date).format('DD/MM/YYYY');
}

export function formatDateToShortFormatWithHyphen(date: string | Date | Dayjs): string {
  return dayjs(date).format('YYYY-MM-DD');
}

export function formatDateOnly(date: string | Date, format: string = 'DD MMM YYYY'): string {
  if (!date) return '';
  const dateStr = typeof date === 'string' && date.includes('T') ? date.split('T')[0] : date;
  return dayjs(dateStr).format(format);
}
